import React, { useEffect } from "react"
import * as Sentry from "@sentry/react"
import { getStaticFeatures } from "../hooks/use-feature"

const allowedHosts = ["app.kitstag.com", "app.kit-qa.com", "app.kit.com"]

const isE2ETest = process.env.CI_E2E_TEST === "true"
const isDeployableEnv = ["production", "staging", "qa"].includes(
  process.env.BUGSNAG_ENV || process.env.NODE_ENV
)

const shouldReportError =
  isDeployableEnv && !isE2ETest && allowedHosts.includes(window.location.host)

if (shouldReportError) {
  Sentry.init({
    dsn: "https://78da087cb2326bd6555d132490a7adbe@o4508813230211072.ingest.us.sentry.io/4510108386590720",
    sendDefaultPii: true,
    release: process.env.SHORT_CIRCLE_SHA1,
    environment: process.env.BUGSNAG_ENV || process.env.NODE_ENV,
    enabled: shouldReportError,
    integrations: [
      Sentry.browserTracingIntegration({
        // Only create spans for API requests, not static assets from CDN
        shouldCreateSpanForRequest: url => {
          // Exclude CDN domains (JS chunks, static assets)
          if (
            url.includes("cdn.kit.com") ||
            url.includes("cdn.kitstag.com") ||
            url.includes("cdn.kit-qa.com") ||
            url.includes("graph.facebook.com")
          ) {
            return false
          }
          // Include our app domains
          return (
            url.includes("kit.com") ||
            url.includes("kitstag.com") ||
            url.includes("kit-qa.com") ||
            url.includes("filekitcdn.com")
          )
        }
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true, // Keep passwords/credit cards masked
        blockAllMedia: false
      })
    ],

    // Tracing
    tracesSampleRate: 0.01,

    tracePropagationTargets: [],

    // Session Replay
    replaysSessionSampleRate: 0.001,
    replaysOnErrorSampleRate: 0.05,

    beforeSend(event, hint) {
      const error = hint.originalException

      // Filter out specific errors
      if (error?.name === "InvalidError") {
        const message = error?.message || error
        if (
          typeof message === "string" &&
          message.startsWith("Object Not Found Matching Id:")
        ) {
          return null
        }
      }

      // Filter Sentry SDK noise: non-error promise rejections wrapped by Sentry
      // Only filter when it originates from Sentry's own helpers.ts sentryWrapped function
      if (
        error?.name === "InvalidError" &&
        error?.message?.includes(
          "unhandledrejection handler received a non-error"
        )
      ) {
        const stacktrace =
          event.exception?.values?.[0]?.stacktrace?.frames || []
        const isFromSentryWrapper = stacktrace.some(
          frame =>
            frame.function === "sentryWrapped" ||
            (frame.filename?.includes("sentry") &&
              frame.filename?.includes("helpers"))
        )
        if (isFromSentryWrapper) {
          return null
        }
      }

      // Filter noisy network-related failures
      const message = (error?.message || "").toLowerCase()
      if (
        (error?.name === "TypeError" &&
          message.toLowerCase().includes("load failed")) ||
        (error?.name === "TypeError" &&
          message.toLowerCase().includes("failed to fetch")) ||
        (error?.name === "AxiosError" &&
          message.toLowerCase().includes("network error"))
      ) {
        return null
      }

      // Track errors to analytics
      if (window.analytics) {
        const user = window.app?.user

        window.analytics.track("error", {
          path: window.location.pathname.replace(/\d+/g, ":id"),
          context: event.contexts,
          user_id: user?.id,
          account_id: user?.account_id,
          class_name: error?.name
        })
      }

      // Custom grouping for property access errors
      if (error?.message) {
        const property = error.message.match(/read\sproperty\s'(\w+)'\sof/)?.[1]

        if (property) {
          event.fingerprint = [`property-access:v1-${property}`]
        }
      }

      // Attach static features
      const features = getStaticFeatures()
      if (features.length > 0) {
        event.contexts = event.contexts || {}
        event.contexts.features = features.reduce(
          (acc, feat) => ({ ...acc, [feat]: true }),
          {}
        )
      }

      return event
    }
  })
}

// Provide fallback ErrorBoundary when Sentry is not initialized
const ErrorBoundary = !shouldReportError
  ? ({ children }) => children
  : Sentry.ErrorBoundary

export function SentryErrorBoundary({ children }) {
  useEffect(() => {
    // Only set user data if Sentry is initialized
    if (!shouldReportError || !window.app) {
      return
    }
    const user = window.app.user

    // Set user identification in Sentry
    Sentry.setUser({
      id: user?.id,
      email: user?.email
    })

    // Add account metadata
    Sentry.setContext("account", {
      id: user?.account_id,
      created_at: user?.account_created_at
    })

    // Add admin context if impersonating
    if (user?.admin) {
      Sentry.setContext("admin", {
        id: window.app.admin_user?.id,
        email: window.app.admin_user?.email
      })
    }
  }, [])

  return <ErrorBoundary>{children}</ErrorBoundary>
}

window.SentryClient = Sentry
export { Sentry }
